import assertDefined from '@/utils/assertDefined'

import { getTutorOfferingPageUrlByWorld } from '@/client/services/programs/helpers'
import { getSubworldEventNotificationURL } from '@/client/services/progression'
import * as url from '@/client/services/url'

import type { NotificationDetails } from '@/components/Worlds/types'

import { SAT_BOOTCAMP_WORLD_SLUG } from '../constants'
import type { NotificationType } from './types'

function getPostUrl(notification: NotificationDetails<NotificationType>) {
  const { id, world, post } = notification
  assertDefined(post, `Post not found for Notification#${id}`)
  assertDefined(post.channel, `Channel not found for Post#${post.id}`)

  return url.getPostUrlFromContext({
    postId: post.id,
    channelId: post.channel.id,
    hasWorld: world != null,
  })
}

const NOTIFICATION_URL_BY_TYPE: {
  [T in NotificationType]: (notification: NotificationDetails<T>) => string
} = {
  assessment_feedback_received: notification => {
    const {
      payload: { assessmentId },
    } = notification
    return url.getViewAssessmentResultsUrl(assessmentId)
  },
  certification_request_approved: notification => {
    const { world } = notification
    return world == null
      ? url.getPortfolioUrl()
      : getTutorOfferingPageUrlByWorld(world.slug, 'certify')
  },
  certification_request_rejected: notification => {
    const { world } = notification
    return world == null
      ? url.getCertificationUrl('get-certified')
      : getTutorOfferingPageUrlByWorld(world.slug, 'certify')
  },
  post_reply_original_poster: notification => getPostUrl(notification),
  post_feedback_received: notification => getPostUrl(notification),
  post_reply_commenter: notification => getPostUrl(notification),
  user_level_up: notification => {
    const { id, world } = notification
    assertDefined(world, `World not found for Notification#${id}`)

    return getTutorOfferingPageUrlByWorld(world.slug)
  },
  tutor_audit_received: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getTutorFeedbackUrl({ sessionId: session.id })
  },
  parent_consent_completed: () =>
    url.getDashboardProgramsUrl({ programSlug: SAT_BOOTCAMP_WORLD_SLUG }),
  // TODO: Make this work for other worlds/programs if we add parent consent elsewhere.

  tutor_followed: notification => {
    const { relatedUser } = notification

    return `${url.getUserUrl({ userId: relatedUser?.id ?? 0, username: relatedUser?.username ?? null })}`
  },
  live_help_response: notification => getPostUrl(notification),

  progression_points_earned: notification => {
    const { userEvent, user } = notification
    assertDefined(userEvent, `No UserEvent found for Notification#${notification.id}`)
    return getSubworldEventNotificationURL({ userEvent, user })
  },
  session_comment: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getSessionUrl(session.id)
  },
  series_comment: notification => {
    const { id, series } = notification

    assertDefined(series, `Series not found for Notification#${id}`)

    return url.getSeriesUrl(series.id)
  },
  audit_reply_received: _ => url.getTutorPeerReviewUrl({ path: 'past' }),
  new_post: notification => getPostUrl(notification),
  session_feedback_received: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getTutorFeedbackUrl({ sessionId: session.id })
  },
  session_feedback_automated_ready: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getSessionFeedbackUrlById(session.id)
  },
  session_feedback_updated: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getTutorFeedbackUrl({ sessionId: session.id })
  },
  session_registration_created: notification => {
    const { id, session } = notification

    assertDefined(session, `Session not found for Notification#${id}`)

    return url.getSessionUrl(session.id)
  },
  series_registration_created: notification => {
    const { id, series } = notification

    assertDefined(series, `Series not found for Notification#${id}`)

    return url.getSeriesUrl(series.id)
  },
  post_event_reaction: notification => getPostUrl(notification),
  post_reaction: notification => getPostUrl(notification),
  post_mention: notification => getPostUrl(notification),
  post_event_mention: notification => getPostUrl(notification),
  volunteer_team_joined: notification => url.getVolunteerTeamUrl(notification.payload.teamSlug),
  volunteer_team_left: notification => url.getVolunteerTeamUrl(notification.payload.teamSlug),
  volunteer_offer_extended: notification =>
    url.getVolunteerRoleApplicationUrl(notification.payload.roleId),
  volunteer_offer_accepted: notification => url.getVolunteerTeamUrl(notification.payload.teamSlug),
  volunteer_offer_declined: _ => url.getVolunteerApplicationReviewUrl(),
  volunteer_application_rejected: notification =>
    url.getVolunteerRoleApplicationUrl(notification.payload.roleId),
}

export default function getNotificationUrl<T extends NotificationType>({
  type,
  notification,
}: {
  type: T
  notification: NotificationDetails<T>
}): string {
  return NOTIFICATION_URL_BY_TYPE[type](notification)
}
